import { memo, useMemo } from "react";

import type { ParsedToken } from "@/lib/search";

import { tokenState, type CardSignal } from "./cardSignal";
import { markEdgesAt, markRuns, type FieldMarks } from "./logFind";
import { isWord, pivotTargets } from "./tokens";

/**
 * A sentence in the reading log, rendered as tokenised furigana ruby with **collection state in the
 * underline** — the single densest piece of information in the design:
 *
 *   solid green    a card is filed under this exact word
 *   dashed green   you hold this word, spelled differently — no card under this string
 *   dotted amber   neither
 *   selected       action tint + a box-shadow underline, and its ruby turns action blue
 *
 * The middle rung is not a shade of "carded": solid means *you own this card* and nothing looser,
 * which is what the `+ add` button beside it reads. Dashed keeps the reading aid the old fuzzy
 * boolean provided — you have met this word — without claiming a card that does not exist. Same
 * hue, weaker line, so the two read as one family at a glance and differ on inspection.
 *
 * The underline uses box-shadow rather than border-bottom on the selected token so it survives the
 * highlight background, and `box-decoration-clone` so a token that wraps keeps both halves styled —
 * the mocks never wrap, so this is a case they do not cover but reading real text does.
 *
 * ## Two layers, and they answer different questions (日本-512)
 *
 * This used to render one `<ruby>` per token and put both the underline and the selection on it.
 * It now renders a `<span>` per CHARACTER inside each token's ruby, because the two things it
 * draws stopped agreeing about where they start and end:
 *
 * - the **underline** is per token — it says what the collection holds, and the collection holds
 *   words;
 * - the **highlight** is per MATCH — it says what the dock is answering, and since the pivot can
 *   be any character the match can begin inside one token and run into the next. Hovering the 道
 *   of 弓道 answers 道場, whose highlight covers the 道 of 弓道 and the whole of 場.
 *
 * Highlighting the host token instead would claim a word boundary the pivot has just contradicted,
 * which is the same mistake in the other direction. Yomitan does exactly this — its scanner sets
 * the highlight to `originalTextLength`, the longest text that actually matched — and it is the
 * only honest answer.
 */
export interface SentenceProps {
  /** Which sentence this is, so `onSelect` can be one stable callback rather than one per row. */
  entryId: string;
  tokens: readonly ParsedToken[];
  /** The raw capture, rendered as-is while the parse is in flight or when it failed — a captured
   * sentence must never display as nothing. */
  text: string;
  /**
   * The run the dock is answering about: where it starts, and how long it is.
   *
   * **This is what the highlight is, and it is deliberately NOT the live pivot.** The pivot moves
   * with the pointer; this moves when the answer lands. Drawing from the pointer instead is what
   * made the highlight bounce — traced over one sweep of `この豪華な弓道場がある。`, the old code
   * produced **16 states across 11 characters**, half of them spans that are not words
   * (`道場が`, `場が`, `華な`) because the new pivot was still wearing the previous word's length.
   *
   * The cost, accepted deliberately: the highlight lags the pointer by the dock's own debounce and
   * round trip, and characters swept past quickly never light at all. That is the intended
   * reading — it settles on the word you stopped on, in step with the dock, rather than chasing
   * every pointer move. See `Lookup.match`.
   *
   * Null when nothing is selected, while the dock is Ask's, or on any sentence that is not the
   * pivoted one.
   */
  match?: { index: number; length: number } | null;
  /**
   * What the log's find band has marked in this sentence (日本-603), in code-point offsets. Only
   * rows with a hit receive it; every other row gets `undefined`, so typing a query does not
   * re-render sentences it never touched.
   */
  findMarks?: FieldMarks;
  /** What is known about each term, so the underline can be right on arrival. */
  signal: ReadonlyMap<string, CardSignal>;
  /**
   * Hover selects the word at this CHARACTER OFFSET in this sentence. Clicking is the SENTENCE's
   * gesture (enter edit state), so tokens deliberately do not handle click — except on a phone,
   * see `selectOnTap`.
   *
   * Takes the entry id so the parent can hold ONE stable callback for the whole log; a closure per
   * row would give every sentence a new prop on every render and defeat the memo above.
   */
  onSelect: (entryId: string, index: number) => void;
  /** px; the active capture renders larger than the scrollback. */
  fontSize: number;
  /**
   * Phones have no hover. A tap has to mean *look this word up*, and it has to stop there — the
   * sentence's own click gesture (enter edit state) would otherwise fire on the same tap and make
   * the word unlookuppable. Editing is reached from the `部` button instead (`6d`).
   */
  selectOnTap?: boolean;
}

/**
 * **Memoised, and that is a performance fix rather than tidiness.**
 *
 * A log is every sentence you have read this session, and each one renders a span per CHARACTER
 * since 日本-512 — roughly ten times the nodes it used to. Unmemoised, every one of them
 * re-rendered each time the dock settled, because `Read` re-renders on every lookup. Measured on
 * one hover: **175ms between the answer arriving and the highlight painting**, of a 300ms
 * hover-to-highlight total that Alex reported as sluggish.
 *
 * The props are shaped to make this actually bite: `match` is passed only to the pivoted sentence
 * (every other gets a constant `null`), `onSelect` is a stable callback that takes the entry id
 * rather than a fresh closure per row, and `tokens`/`signal` are already referentially stable
 * across a dock change. So a dock answer re-renders exactly one sentence.
 */
export const Sentence = memo(function Sentence({
  entryId,
  tokens,
  text,
  match,
  findMarks,
  signal,
  onSelect,
  fontSize,
  selectOnTap = false,
}: SentenceProps) {
  // Which absolute offsets are pivotable, as a map so the per-character render is a lookup rather
  // than a scan per glyph. `pivotTargets` owns the rule; this file only draws it.
  //
  // Memoised on the tokens: it walks every character of every token, and a sentence re-renders
  // whenever its own highlight moves — recomputing the whole set to move a two-character span is
  // the largest avoidable cost in this component.
  const targets = useMemo(
    () => new Map(pivotTargets(tokens).map((t) => [t.index, t])),
    [tokens],
  );

  // The highlighted run: [from, to). Both ends come from the same answer — see `match`. Clamped to
  // the sentence so an answer that arrives after an edit shortened the text cannot paint past it.
  const total = tokens.reduce((n, t) => Math.max(n, t.start + t.length), 0);
  const from = match ? match.index : null;
  const to = match ? Math.min(total, match.index + Math.max(1, match.length)) : null;

  return (
    <p
      className="font-jp"
      // Line-height must stay >= 1.9 to leave room for ruby; the design uses 1.9-2.05.
      style={{ fontSize, lineHeight: fontSize >= 28 ? 2.05 : 1.9 }}
    >
      {tokens.length === 0 && (
        <span className="text-ink-4">
          {markRuns(text, findMarks).map((run, i) =>
            run.mark ? (
              <span key={i} data-find={run.mark} style={FIND_STYLE[run.mark]}>
                {run.text}
              </span>
            ) : (
              run.text
            ),
          )}
        </span>
      )}
      {tokens.map((token, index) => {
        if (!isWord(token)) {
          // Punctuation and Latin are searchable too (日本-603): `findHits` matches the whole
          // text, so a hit on `。` or `！？` must be drawn here or the count says one thing and the
          // sentence another.
          const chars = [...token.surface];
          const marked = findMarks && chars.some((_, i) => markEdgesAt(findMarks, token.start + i));
          return (
            <span key={index} className="text-ink-4">
              {marked
                ? chars.map((char, i) => {
                    const edges = markEdgesAt(findMarks, token.start + i);
                    if (!edges) return char;
                    return (
                      <span
                        key={i}
                        data-find={edges.kind}
                        style={{
                          ...FIND_STYLE[edges.kind],
                          ...(edges.kind === "current" ? { boxShadow: runRing(edges) } : null),
                        }}
                      >
                        {char}
                      </span>
                    );
                  })
                : token.surface}
            </span>
          );
        }

        // Both keys, stronger wins — see `tokenState`, which is exported so it can be tested
        // without a DOM.
        const state = tokenState(signal, token);
        const underline =
          state === "known"
            ? "2px solid var(--ink-3)"
            : state === "elsewhere"
              ? "2px dashed var(--ink-3)"
              : "2px dotted var(--ink-4)";

        /**
         * One character, drawn with its own pivot handler and its own share of the highlight.
         *
         * `offset` is absolute — an index into the sentence, which is what the server's `start`
         * values and `POST /api/search/lookup`'s `index` both are. Deriving it per glyph rather
         * than threading a running cursor keeps every character independent of the ones before it,
         * which matters because furigana splits a token into segments of differing length.
         */
        const character = (char: string, offset: number, key: string) => {
          const target = targets.get(offset);
          // **Every character of a word is hoverable; only a TARGET aims at itself.** A kana
          // interior is not a pivot target — pivoting the し of 楽しんでいる answers 75 rows of
          // 新 / 真 / 信 / 辛 — but it must still select the word it is inside, which is what
          // hovering anywhere in a token did before the pivot became a character. Without the
          // fallback, five of 楽しんでいる's six characters are dead: the pointer entering the
          // line from above lands mid-word and the dock keeps answering the previous one, and on
          // a phone the tap falls through to the wrapper and opens the sentence for editing.
          // `TAP_BOX`'s own argument — "every neighbour is also a valid target, never a dead
          // zone" — is only true of an all-kanji word without this.
          const pivot = target ? offset : token.start;
          const lit = from !== null && to !== null && offset >= from && offset < to;
          // The find band's mark, under the dock's highlight: a character both answer for shows
          // the dock's, because that is the one that moves as you read.
          const edges = lit ? null : markEdgesAt(findMarks, offset);
          const found = edges?.kind ?? null;
          return (
            <span
              key={key}
              data-check="read.char"
              // Only a real target is advertised as one: `pivotTargets` owns that rule and
              // `check-search.mjs` reads this attribute to assert a kana interior is not one.
              data-pivot={target ? offset : undefined}
              data-lit={lit || undefined}
              data-find={found ?? undefined}
              onMouseEnter={() => onSelect(entryId, pivot)}
              onClick={
                selectOnTap
                  ? (event) => {
                      event.stopPropagation();
                      onSelect(entryId, pivot);
                    }
                  : undefined
              }
              className="cursor-pointer"
              style={
                lit
                  ? {
                      background: "var(--accent-tint)",
                      boxShadow: "0 2px 0 var(--accent)",
                      boxDecorationBreak: "clone",
                      WebkitBoxDecorationBreak: "clone",
                      // **A phone tap needs a 44px box and the line must not move.** The glyph is
                      // 24px there, so the box is grown with padding and pulled back out with an
                      // equal negative margin: the hit area is real, and every rect around it is
                      // exactly where it was. Measured before and after.
                      ...(selectOnTap ? TAP_BOX : null),
                    }
                  : found
                    ? {
                        ...FIND_STYLE[found],
                        ...(found === "current" && edges ? { boxShadow: runRing(edges) } : null),
                        ...(selectOnTap ? TAP_BOX : null),
                      }
                    : selectOnTap
                      ? TAP_BOX
                      : undefined
              }
            >
              {char}
            </span>
          );
        };

        // Furigana splits the token into segments; a segment either carries ruby (and is drawn as
        // its own nested <ruby>) or is plain text. Either way its characters have to keep their
        // absolute offsets, so the cursor walks the token rather than the sentence.
        let cursor = token.start;
        const anySelected = from !== null && to !== null && from < token.start + token.length && to > token.start;

        return (
          <ruby
            key={index}
            data-selected={anySelected || undefined}
            className="[&>rt]:data-selected:text-accent-text"
            // The underline is the TOKEN's, and it stays on the token — it says what the
            // collection holds, and the collection holds words. The highlight above is the
            // match's and is drawn per character inside.
            style={{ borderBottom: anySelected ? undefined : underline }}
          >
            {token.furigana.length > 0
              ? token.furigana.map((segment, i) => {
                  const chars = [...segment.text];
                  const at = cursor;
                  cursor += chars.length;
                  return segment.ruby ? (
                    <ruby key={i}>
                      {chars.map((c, j) => character(c, at + j, `${i}-${j}`))}
                      <rt style={anySelected ? { color: "var(--accent-deep)" } : undefined}>
                        {segment.ruby}
                      </rt>
                    </ruby>
                  ) : (
                    <span key={i}>{chars.map((c, j) => character(c, at + j, `${i}-${j}`))}</span>
                  );
                })
              : [...token.surface].map((c, i) => character(c, token.start + i, `s-${i}`))}
          </ruby>
        );
      })}
    </p>
  );
});

/**
 * A taller touch box that costs no layout — and it grows on ONE AXIS only.
 *
 * A target is one character now rather than a whole word, and at the phone's 24px reading size
 * that is a small thing to hit. Padding grows the box on the block axis and the equal negative
 * margin puts every neighbouring rect back exactly where it was. Measured on the 道 of 弓道 at
 * 390px: **27 × 55**.
 *
 * **The 44px floor is met vertically and cannot be met horizontally**, and that is a property of
 * the writing rather than an omission. `padding-inline` would put a gap between every live
 * character in the sentence — Japanese is set without spaces, and spacing the glyphs out is a
 * worse reading experience than a narrow target. So the width is the glyph's, always.
 *
 * What makes 27px acceptable is that **every neighbour is also a valid target**: a mis-tap on
 * 弓道 selects 弓 instead of 道 and answers 弓道場 instead of 道場 — a different lookup, one tap
 * from being corrected, never a dead zone. The 44px guidance is written for discrete controls
 * with nothing between them, which this is not. Worth re-measuring if the phone's reading size
 * ever drops below 24px.
 */
/**
 * The find band's two marks (日本-603, canvas frame A). Every hit is a muted fill; the current one
 * takes the accent tint and a 1px accent ring, so it reads as the one you are on without competing
 * with the dock's own highlight, which is the tint plus a 2px underline.
 */
export const FIND_STYLE = {
  hit: { background: "var(--line-strong)", borderRadius: 1 },
  current: {
    background: "var(--accent-tint)",
    boxShadow: "0 0 0 1px var(--accent)",
    color: "var(--ink-bright)",
    borderRadius: 1,
  },
} as const;

/**
 * The current hit's outline, drawn per character as the part of one ring that character owns. Inset
 * so a neighbour's box cannot paint over it.
 */
function runRing({ first, last }: { first: boolean; last: boolean }): string {
  const ring = "var(--accent)";
  return [
    `inset 0 1px 0 ${ring}`,
    `inset 0 -1px 0 ${ring}`,
    first ? `inset 1px 0 0 ${ring}` : null,
    last ? `inset -1px 0 0 ${ring}` : null,
  ]
    .filter(Boolean)
    .join(", ");
}

const TAP_BOX = {
  paddingBlock: 10,
  marginBlock: -10,
} as const;
